import { useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';

// "Envoyer un rappel" button + form for a task: the PMO (admins) or the
// project's PMO accompanist reminds the task's responsible, as a Slack direct
// message and an e-mail (both ticked by default, either can be unticked) with
// an optional note (POST /tasks/:id/remind). Recorded in the task's thread.
// When no ticked channel gets through, nothing is sent and the reason is
// shown; when only one does, the other's failure is shown alongside. Renders
// nothing unless the server said the user may remind (`can_remind`, which also
// implies the task has a responsible).
const ERROR_KEYS: Record<string, string> = {
  NO_ASSIGNEE: 'tasks.remind.err.noAssignee',
  NO_CHANNEL: 'tasks.remind.err.noChannel',
  ASSIGNEE_NO_ACCOUNT: 'tasks.remind.err.noAccount',
  SLACK_DISABLED: 'tasks.remind.err.slackDisabled',
  NOT_ON_SLACK: 'tasks.remind.err.notOnSlack',
  SLACK_FAILED: 'tasks.remind.err.slackFailed',
  EMAIL_FAILED: 'tasks.remind.err.emailFailed',
  REMIND_FORBIDDEN: 'tasks.remind.err.forbidden',
};

const CHANNEL_KEYS: Record<string, string> = { slack: 'tasks.remind.viaSlack', email: 'tasks.remind.viaEmail' };

export function RemindTaskAssignee({ taskId, canRemind, assigneeName, onSent }: {
  taskId: number;
  canRemind: boolean | undefined;
  assigneeName: string | null | undefined;
  /** Called after sending — e.g. to reload the task's thread. */
  onSent?: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [viaSlack, setViaSlack] = useState(true);
  const [viaEmail, setViaEmail] = useState(true);
  const [sending, setSending] = useState(false);

  if (!canRemind || !assigneeName) return null;
  const name = assigneeName;
  const errorText = (code: string) => (ERROR_KEYS[code] ? t(ERROR_KEYS[code], { name }) : code);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.remindTaskAssignee(taskId, note.trim(), { slack: viaSlack, email: viaEmail }) as
        { sent: string[]; failed: Record<string, string> };
      setOpen(false);
      setNote('');
      const via = res.sent.map((c) => t(CHANNEL_KEYS[c])).join(t('tasks.remind.and'));
      const failures = Object.entries(res.failed ?? {}).map(([c, code]) =>
        t('tasks.remind.channelFailed', { channel: t(CHANNEL_KEYS[c]), reason: errorText(code) }));
      alert([t('tasks.remind.sent', { name, via }), ...failures].join('\n\n'));
      onSent?.();
    } catch (err: any) {
      alert(ERROR_KEYS[err.code] ? `${errorText(err.code)} ${t('tasks.remind.nothingSent')}` : err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} title={t('tasks.remind.help', { name })}>
        <BellRing size={14} /> {t('tasks.remind.button')}
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('tasks.remind.title', { name })}</h3>
              <button className="close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('tasks.remind.noteLabel')}</label>
                <textarea
                  className="form-input"
                  rows={4}
                  autoFocus
                  value={note}
                  placeholder={t('tasks.remind.notePlaceholder')}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.remind.channelsLabel')}</label>
                <div style={{ display: 'flex', gap: 'var(--s16)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={viaSlack} onChange={(e) => setViaSlack(e.target.checked)} />
                    <span>{t('tasks.remind.slack')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={viaEmail} onChange={(e) => setViaEmail(e.target.checked)} />
                    <span>{t('tasks.remind.email')}</span>
                  </label>
                </div>
              </div>
              <p className="text-sm text-ink-3" style={{ margin: 0 }}>{t('tasks.remind.explain', { name })}</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-accent" disabled={sending || (!viaSlack && !viaEmail)} onClick={send}>{t('common.send')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
